import i18next from "i18next"
import {initReactI18next} from "react-i18next"
import resourcesToBackend from "i18next-resources-to-backend"
import countries from "i18n-iso-countries"
import moment from "moment"
import {Locale} from "antd/lib/locale-provider"
import {i18nImports} from "./i18nImports"

export const getAntdLocale = async (lang: string): Promise<Locale> => {
    const module = await i18nImports[lang].antd()
    return module.default
}

const defaultNamespaces = [
    "common",
    "glossary",
    "person",
    "home_portal",
    "shared_components",
    "greetings",
    "content",
    "comments",
    "error404",
    "country",
]

type I18nextInstanceConfig = {
    ns: string[]
    lng?: string
}

export const DEFAULT_LANGUAGE = "en"

export const i18n = i18next.createInstance()

export const initI18n = async (config: I18nextInstanceConfig): Promise<void> => {
    const ns = [...defaultNamespaces, ...config.ns]
    const lng = config.lng
    const fallbackLng = DEFAULT_LANGUAGE
    const libImports = i18nImports[lng || fallbackLng]

    try {
        const file = await libImports.countries()
        countries.registerLocale(file)
    } catch (error) {
        console.warn(`Failed to load countries data for language ${lng}`)
    }

    try {
        await libImports.moment()
    } catch (error) {
        console.warn(`Locale ${lng} for moment.js not found, falling back to ${fallbackLng}`)
    }

    moment.updateLocale(lng || fallbackLng, {
        week: {
            dow: 1,
        },
    })

    moment.locale(lng || fallbackLng)

    await i18n
        .use(
            resourcesToBackend(
                (language: string, namespace: string) =>
                    import(`./locales/${language}/${namespace}.json`)
            )
        )
        .use(initReactI18next)
        .init({
            ns,
            lng: lng, // if you're using a language detector, do not define the lng option
            fallbackLng: fallbackLng,
            interpolation: {
                escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
            },
            debug: false,
            preload: lng ? [lng] : [fallbackLng],
            partialBundledLanguages: true,
            react: {
                transSupportBasicHtmlNodes: true,
            },
        })
}
